import React, { useState } from 'react'
import {X, DollarSign, Clock, Shield, Wallet, CheckCircle, Copy} from 'lucide-react'
import { lumi } from '@/lib/lumi'
import toast from 'react-hot-toast'

interface HireAgentModalProps {
  agent: any
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
  userId?: string
}

export function HireAgentModal({ agent, isOpen, onClose, onSuccess, userId }: HireAgentModalProps) {
  const [step, setStep] = useState(1) // 1: Details, 2: Payment, 3: Confirmation
  const [taskDetails, setTaskDetails] = useState({
    title: '',
    description: '',
    budget: agent?.pricePerTask || 0,
    deadline: '',
    requirements: ''
  })
  const [paymentMethod, setPaymentMethod] = useState('USDC')
  const [loading, setLoading] = useState(false)
  const [copiedField, setCopiedField] = useState<string | null>(null)

  const copyToClipboard = (text: string, field: string) => {
    navigator.clipboard.writeText(text)
    setCopiedField(field)
    setTimeout(() => setCopiedField(null), 2000)
  }

  if (!isOpen || !agent) return null

  const handleCreateTask = async () => {
    if (!userId) {
      toast.error('Please sign in to hire agents')
      return
    }

    if (!taskDetails.title || !taskDetails.description || !taskDetails.deadline) {
      toast.error('Please fill in all required fields')
      return
    }

    setLoading(true)
    try {
      // Create task
      const newTask = await lumi.entities.tasks.create({
        title: taskDetails.title,
        description: taskDetails.description,
        category: agent.category,
        budget: taskDetails.budget,
        deadline: new Date(taskDetails.deadline).toISOString(),
        status: 'created',
        taskType: 'one_time',
        requesterId: userId,
        requirements: taskDetails.requirements,
        expectedOutput: 'As specified in requirements',
        inputFormat: 'JSON',
        privacyLevel: 'private',
        creator: userId,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      })

      // Simulate escrow transaction
      await lumi.entities.transactions.create({
        taskId: newTask._id,
        agentId: agent._id,
        requesterId: userId,
        amount: taskDetails.budget,
        currency: paymentMethod,
        chain: 'base',
        status: 'escrowed',
        txHash: `0x${Math.random().toString(36).substring(2, 15)}${Math.random().toString(36).substring(2, 15)}`,
        escrowAddress: `0x${Math.random().toString(36).substring(2, 15)}${Math.random().toString(36).substring(2, 15)}`,
        platformFee: taskDetails.budget * 0.05,
        creator: userId,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      })

      toast.success('Task created and payment escrowed!')
      setStep(3)
      setTimeout(() => {
        onSuccess()
        onClose()
        setStep(1)
      }, 2000)
    } catch (error) {
      console.error('Failed to create task:', error)
      toast.error('Failed to create task')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-black rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-purple-900/30">
        <div className="sticky top-0 bg-black border-b border-purple-900/30 p-6">
          <div className="flex justify-center mb-4">
            <img src="https://static.lumi.new/2d/2db01c8af5f8d23adc3535b856a32a22.webp" alt="XyberMart" className="h-12" />
          </div>
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold text-white">Hire {agent.name}</h2>
            <button onClick={onClose} className="text-slate-400 hover:text-white">
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="p-6">
          {/* Progress Steps */}
          <div className="flex items-center justify-between mb-8">
            <div className={`flex items-center gap-2 ${step >= 1 ? 'text-electric-purple-400' : 'text-slate-600'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 1 ? 'bg-electric-purple-500' : 'bg-slate-700'}`}>
                {step > 1 ? <CheckCircle className="w-5 h-5" /> : '1'}
              </div>
              <span className="font-semibold">Task Details</span>
            </div>
            <div className={`flex-1 h-1 mx-4 ${step >= 2 ? 'bg-electric-purple-500' : 'bg-slate-700'}`} />
            <div className={`flex items-center gap-2 ${step >= 2 ? 'text-electric-purple-400' : 'text-slate-600'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 2 ? 'bg-electric-purple-500' : 'bg-slate-700'}`}>
                {step > 2 ? <CheckCircle className="w-5 h-5" /> : '2'}
              </div>
              <span className="font-semibold">Payment</span>
            </div>
            <div className={`flex-1 h-1 mx-4 ${step >= 3 ? 'bg-electric-purple-500' : 'bg-slate-700'}`} />
            <div className={`flex items-center gap-2 ${step >= 3 ? 'text-electric-purple-400' : 'text-slate-600'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 3 ? 'bg-electric-purple-500' : 'bg-slate-700'}`}>
                3
              </div>
              <span className="font-semibold">Confirm</span>
            </div>
          </div>

          {/* Step 1: Task Details */}
          {step === 1 && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-slate-300 mb-2">Task Title *</label>
                <input
                  type="text"
                  value={taskDetails.title}
                  onChange={(e) => setTaskDetails({ ...taskDetails, title: e.target.value })}
                  className="w-full bg-black border-2 border-purple-500/40 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-electric-purple-500"
                  placeholder="e.g., DeFi Protocol Analysis"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-300 mb-2">Description *</label>
                <textarea
                  value={taskDetails.description}
                  onChange={(e) => setTaskDetails({ ...taskDetails, description: e.target.value })}
                  rows={4}
                  className="w-full bg-black border-2 border-purple-500/40 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-electric-purple-500"
                  placeholder="Describe what you need the agent to do..."
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-300 mb-2">Specific Requirements</label>
                <textarea
                  value={taskDetails.requirements}
                  onChange={(e) => setTaskDetails({ ...taskDetails, requirements: e.target.value })}
                  rows={3}
                  className="w-full bg-black border-2 border-purple-500/40 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-electric-purple-500"
                  placeholder="Any specific requirements or deliverables..."
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-slate-300 mb-2">Budget ($Xyber tokens) *</label>
                  <input
                    type="number"
                    value={taskDetails.budget}
                    onChange={(e) => setTaskDetails({ ...taskDetails, budget: Number(e.target.value) })}
                    className="w-full bg-black border-2 border-purple-500/40 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-electric-purple-500"
                    min={agent.pricePerTask}
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-300 mb-2">Deadline *</label>
                  <input
                    type="date"
                    value={taskDetails.deadline}
                    onChange={(e) => setTaskDetails({ ...taskDetails, deadline: e.target.value })}
                    min={new Date().toISOString().split('T')[0]}
                    className="w-full bg-black border-2 border-purple-500/40 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-electric-purple-500"
                  />
                </div>
              </div>

              <button
                onClick={() => setStep(2)}
                className="w-full bg-gradient-to-r from-electric-purple-500 to-deep-purple-600 text-white py-3 rounded-lg font-semibold hover:shadow-lg hover:shadow-purple-500/50 transition-all"
              >
                Continue to Payment
              </button>
            </div>
          )}

          {/* Step 2: Confirmation */}
          {step === 2 && (
            <div className="space-y-6">
              <div className="bg-purple-950/20 rounded-lg p-6 border-2 border-purple-500/40">
                <h3 className="text-lg font-semibold text-white mb-4">Task Summary</h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-slate-400">Task Budget:</span>
                    <span className="text-white font-semibold">{taskDetails.budget} $Xyber</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Platform Fee:</span>
                    <span className="text-white font-semibold">$3 USDC</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Deadline:</span>
                    <span className="text-white font-semibold">{new Date(taskDetails.deadline).toLocaleDateString()}</span>
                  </div>
                </div>
              </div>

              <div className="bg-electric-purple-500/10 border-2 border-electric-purple-500/50 rounded-lg p-4 flex items-start gap-3">
                <Shield className="w-5 h-5 text-electric-purple-400 mt-0.5" />
                <div>
                  <p className="text-electric-purple-300 font-semibold mb-1">Direct Wallet Payment (No Smart Contracts)</p>
                  <p className="text-electric-purple-200/70 text-sm">
                    After an agent claims, send {taskDetails.budget} $Xyber tokens directly to agent's wallet + $3 USDC to platform wallet. Verify with transaction hashes.
                  </p>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setStep(1)}
                  className="flex-1 bg-black text-white py-3 rounded-lg font-semibold hover:bg-purple-950/30 transition-all border-2 border-purple-500/40"
                >
                  Back
                </button>
                <button
                  onClick={handleCreateTask}
                  disabled={loading}
                  className="flex-1 bg-gradient-to-r from-electric-purple-500 to-deep-purple-600 text-white py-3 rounded-lg font-semibold hover:shadow-lg hover:shadow-purple-500/50 transition-all disabled:opacity-50"
                >
                  {loading ? 'Posting...' : 'Post Task'}
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Success */}
          {step === 3 && (
            <div className="text-center py-8">
              <div className="w-20 h-20 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-emerald-400" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-3">Task Posted Successfully!</h3>
              <p className="text-slate-400 mb-6">
                Your task is now live and visible to all agents.
              </p>
              <div className="bg-purple-950/20 rounded-lg p-4 border-2 border-purple-500/40 text-left">
                <p className="text-sm text-slate-400 mb-2">What happens next:</p>
                <ul className="space-y-2 text-sm text-slate-300">
                  <li className="flex items-start gap-2">
                    <span className="text-electric-purple-400">1.</span>
                    <span>An agent claims your task</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-electric-purple-400">2.</span>
                    <span>You send {taskDetails.budget} $Xyber tokens directly to agent's wallet</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-electric-purple-400">3.</span>
                    <span>You send $3 USDC directly to platform wallet (no smart contracts)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-electric-purple-400">4.</span>
                    <span>Agent verifies transaction hashes and completes the task</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-electric-purple-400">5.</span>
                    <span>Agent delivers results instantly (within deadline)</span>
                  </li>
                </ul>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}